import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { getStatusColor, getStatusLabel } from '../positions/positionStatus';
import { positionTitle } from './fairHelpers';

const TIPOS = ['massive', 'hunting'];
const STATUSES = ['active', 'draft', 'suspended'];

/**
 * Selector de vacantes para una feria (PLAN_FERIAS). Muestra solo las que el
 * usuario puede agregar (el backend filtra por sus proyectos asignados,
 * confidencialidad y estado): búsqueda, filtros por cliente, tipo y estado, y
 * selección múltiple. Las que ya están en la feria aparecen deshabilitadas.
 *
 * Props: open, onClose, excludeIds (ya en la feria), onAdd(items) en el orden
 * en que se marcaron.
 */
const AtsFairPositionPicker = ({ open, onClose, excludeIds = [], onAdd }) => {
  const { t } = useTranslation();
  const { organization, authToken } = useStore.getState();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [client, setClient] = useState('');
  const [tipo, setTipo] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState([]);

  // Cada apertura recarga la lista y parte sin selección
  useEffect(() => {
    if (!open) return;
    setSelected([]);
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/organizations/job-fairs/available-positions', {
          params: { organizationId: organization },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = response.data.response;
        if (data?.status_response === 'OK') setItems(data.data || []);
        else setError(t('ats.fairs.picker.loadError'));
      } catch (err) {
        console.error(err);
        setError(t('ats.fairs.picker.loadError'));
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const clients = useMemo(
    () => [...new Set(items.map((i) => i.projectName).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [items]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((i) => (
      (!client || i.projectName === client)
      && (!tipo || i.tipo === tipo)
      && (!status || i.status === status)
      && (!term || [i.internalName, i.name, i.projectName, i.ubicacion]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(term)))
    ));
  }, [items, search, client, tipo, status]);

  const excluded = new Set(excludeIds);

  const toggle = (uid) => {
    if (excluded.has(uid)) return;
    setSelected((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));
  };

  const handleAdd = () => {
    const byId = new Map(items.map((i) => [i.uid, i]));
    onAdd(selected.map((uid) => byId.get(uid)).filter(Boolean));
  };

  const filterSelect = (label, value, onChange, options) => (
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        <MenuItem value="">{t('ats.fairs.picker.all')}</MenuItem>
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('ats.fairs.picker.title')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('ats.fairs.picker.hint')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            size="small"
            placeholder={t('ats.fairs.picker.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: '1 1 240px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {filterSelect(t('ats.fairs.picker.client'), client, setClient, clients.map((c) => ({ value: c, label: c })))}
          {filterSelect(t('ats.fairs.picker.type'), tipo, setTipo, TIPOS.map((v) => ({ value: v, label: t(`ats.positions.wizard.options.tipo.${v}`) })))}
          {filterSelect(t('ats.fairs.picker.status'), status, setStatus, STATUSES.map((v) => ({ value: v, label: getStatusLabel(v, t) })))}
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && filtered.length === 0 && (
          <Alert severity="info">{t('ats.fairs.picker.empty')}</Alert>
        )}

        {!loading && !error && filtered.map((item) => {
          const already = excluded.has(item.uid);
          const checked = already || selected.includes(item.uid);
          return (
            <Box
              key={item.uid}
              data-testid={`picker-item-${item.uid}`}
              onClick={() => toggle(item.uid)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.75,
                px: 1,
                borderRadius: 1,
                cursor: already ? 'default' : 'pointer',
                opacity: already ? 0.6 : 1,
                '&:hover': { backgroundColor: already ? 'transparent' : 'rgba(106,90,249,0.06)' },
              }}
            >
              <Checkbox size="small" checked={checked} disabled={already} tabIndex={-1} />
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{positionTitle(item)}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {[item.projectName, item.ubicacion, t(`ats.positions.wizard.options.tipo.${item.tipo}`)].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {already && <Chip size="small" variant="outlined" label={t('ats.fairs.picker.alreadyIn')} />}
                {!item.visibleInPortal && (
                  <Chip size="small" color="warning" variant="outlined" label={t('ats.fairs.picker.notVisible')} />
                )}
                <Chip size="small" color={getStatusColor(item.status)} label={getStatusLabel(item.status, t)} />
              </Box>
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={selected.length === 0}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
        >
          {t('ats.fairs.picker.add', { count: selected.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AtsFairPositionPicker;
